import React, { useState, useEffect } from "react";
import CollapseIcon from "../components/data/CollapseIcon.png";
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import TextField from '@mui/material/TextField';
import SendIcon from '@mui/icons-material/Send';

// Handles what happens when a user clicks the like (heart) button 
const likeUnlike = (index, userID, setAllQuestionsData, setLastUpdatedIndex) => {
    setAllQuestionsData(prevQuestionsData => {
        // Map through the previous state to create a new array
        return prevQuestionsData.map((q, i) => {
            // Check if the current map iteration is the index we want to update
            if (i === index) {
                // Handle the logic for liking/unliking the question
                if (!q.usersVoted) {
                    // No users have voted yet, create the array and add the user
                    return {
                        ...q,
                        usersVoted: [userID],
                        numVotes: (q.numVotes || 0) + 1, // Ensure numVotes is a number and increment
                    };
                } else if (!q.usersVoted.includes(userID)) {
                    // User has not voted yet, add the user to the array
                    return {
                        ...q,
                        usersVoted: [...q.usersVoted, userID],
                        numVotes: q.numVotes + 1, // Increment numVotes
                    };
                } else {
                    // User is unliking the question now
                    return {
                        ...q,
                        usersVoted: q.usersVoted.filter(id => id !== userID),
                        numVotes: q.numVotes - 1,
                    };
                }
            }
            return q; // Return the question unchanged if it's not the one we're updating
        });
    });
    setLastUpdatedIndex(index); // This will trigger the useEffect after state update
};


// Calls API to increase or decrease the number of votes. This API automatically checks 
// if the current user has voted and decrease/increases accordingly.
async function updateVoteAPI(voteId, userId) {
    try {
        const response = await fetch(`https://versify-back-b066d2bc73d1.herokuapp.com/vote/${voteId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ userId }),
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.message || 'Failed to update vote.');
        }
        return data;
    } catch (error) {
        console.error('Error updating vote:', error);
    }
}

// This function is renders the panel on the LHS. It can toggle between two views
// 1. Information about the author + call to actions to subscribe to this author
// 2. The user's chat history 
// This panel is collapseable 
function InfoPanel({ showInfoPanel, setShowInfoPanel, allQuestionsData, setAllQuestionsData, userID, panelImageURL, storyID, panelView, setPanelView }) {
    // const [panelView, setPanelView] = useState("author"); // Tracks which view we should display on LHS panel 
    const [lastUpdatedIndex, setLastUpdatedIndex] = useState(null);

    const [comment, setComment] = useState(''); // State to store the messages users sends 

    // console.log('allQuestionsData', allQuestionsData);

    // This effect will run after each render if allQuestionsData changes and call an API to update the vote count 
    useEffect(() => {
        if (lastUpdatedIndex !== null) {
            const updatedQuestion = allQuestionsData[lastUpdatedIndex];
            if (updatedQuestion && updatedQuestion._id) {
                updateVoteAPI(updatedQuestion._id, userID) // Make sure you have userID in scope
                    .then(result => {
                        // console.log(result);
                    })
                    .catch(error => {
                        // console.error('Failed to update vote:', error);
                    });
            }
            setLastUpdatedIndex(null); // Reset after handling
        }
    }, [allQuestionsData, lastUpdatedIndex]);

    // Display questions LHS panel 
    const showQuestions = () => {
        ; if (panelView === "author") {
            setPanelView("questions");
        }
    }

    // Display author info  LHS panel 
    const showAuthorPanel = () => {
        if (panelView === "questions") {
            setPanelView("author");
        }
    }

    // Handles what happens if user clicks to minimize/expand the infoPanel
    const collapseExpand = () => {
        setShowInfoPanel(prevShowInfoPanel => !prevShowInfoPanel); // Flips the value
    }



    // Defines how a component for each question should be rendered
    const QuestionComponent = ({ index, question, currentUserID, likeUnlike }) => { // likeUnlike is now included in the parameters
        // Render the individual question data as needed
        return (
            <div className="question" style={{ display: 'flex', justifyContent: 'space-between' }}>
                <p>{question.queriedText}</p>
                <div
                    style={{
                        color: '#6f6f6f',
                        display: 'flex',       // Enable flexbox
                        alignItems: 'center',  // Align items vertically
                        marginLeft: 'auto',    // This pushes the div to the right
                    }}>
                    {question.usersVoted && question.usersVoted.includes(currentUserID) ? (
                        <>
                            <FavoriteIcon style={{ cursor: 'pointer', color: 'red' }} onClick={() => likeUnlike(index, currentUserID, setAllQuestionsData, setLastUpdatedIndex)} />
                            <span style={{ marginLeft: '8px' }}>{question.numVotes}</span>
                        </>
                    ) : (
                        <>
                            <FavoriteBorderIcon style={{ cursor: 'pointer' }} onClick={() => likeUnlike(index, currentUserID, setAllQuestionsData, setLastUpdatedIndex)} />
                            <span style={{ marginLeft: '8px' }}>{question.numVotes}</span>
                        </>
                    )}
                </div>
            </div>
        );
    };

    // Sends a comment if user presses enter
    const handleKeyPress = (event) => {
        if (event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();  // This prevents the event from bubbling up so that next point isn't called
            postComment();
        }
    };

    // Handles what happens when users post a comment
    const postComment = async () => {
        if (comment.trim() !== "") {
            // console.log('comment', comment);

            const newQuestionsData = [
                ...allQuestionsData,
                {
                    numVotes: 0,
                    queriedText: comment,
                    storyId: storyID, 
                    topicId: "", // specify if needed
                    userId: userID,
                    usersVoted: []
                }
            ];
            setAllQuestionsData(newQuestionsData); // Cause a re-render with the new question added

            // Scroll to bottom of the questions container
            setTimeout(() => {
                const questionsContainerElement = document.querySelector('.questionsContainer');
                if (questionsContainerElement) {
                    questionsContainerElement.scrollTop = questionsContainerElement.scrollHeight;
                }
            }, 0);

            await postQuestion(userID, storyID, comment); // Write comment to db 
            setComment(''); // Clear the message input
        }
    };

    // Post the comment to the database
    async function postQuestion(userId, storyId, queriedText) {
        const url = 'https://versify-back-b066d2bc73d1.herokuapp.com/question'; // URL of the endpoint, adjust if necessary (e.g., if you need a full URL)
        
        try {
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ userId, storyId, queriedText})
          });
      
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const result = await response.json(); // Assuming the server sends back a JSON response
        //   console.log('Question posted successfully:', result);
          return result; // return the result for further processing if necessary
        } catch (error) {
          console.error('Error posting question:', error);
        }
      }
      

    return (
        <div style={{ height: "100%", position: 'relative' }}>
            {/** Button that collapses sidebar */}
            <img
                src={CollapseIcon}
                style={{
                    position: 'absolute',
                    top: '25px',
                    right: '25px',
                    height: '25px',
                    zIndex: 1000,
                    cursor: 'pointer'
                }}
                alt="Collapse Button"
                onClick={collapseExpand}
            />
            {/** Display image with author's info only if user has toggled to show author info*/}

            <img className='AuthorPanelImage' src={panelImageURL} alt="AuthorPanel" />
    
            <div
                style={{
                    position: 'absolute',
                    left: 0, // spans from left
                    right: 0, // spans to right
                    bottom: '90px', // ends 80px above bottom
                    display: 'flex',
                    justifyContent: 'center', // horizontally center
                }} >
                {/* <div id="AuthorInfo" className="panelToggles"
                    style={{ borderLeft: "1px solid #E8E8E8", cursor: "pointer" }}
                    onClick={showAuthorPanel}
                >
                    <div className={`panelToggles ${panelView === 'author' ? 'toggleOn' : 'toggleOff'}`}>
                        Author
                    </div> */}
                {/* </div> */}
                {/* <div id="QuestionsInfo" className="panelToggles"
                    style={{ borderRight: "1px solid #E8E8E8", borderRadius: "0px 8px 8px 0px", cursor: "pointer" }}
                    onClick={showQuestions}
                > */}
                    {/* <div className={`panelToggles ${panelView === 'questions' ? 'toggleOn' : 'toggleOff'}`}>
                        Questions
                    </div>
                </div> */}
            </div>
        </div>
    )
}


export default InfoPanel;
